import { Video } from 'lucide-react';
import { type Control, Controller, useWatch } from 'react-hook-form';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { Field, FieldLabel, FieldSlot } from '@nesso/ui/primitives/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@nesso/ui/primitives/input-group';
import { MaskedInputField, US_PHONE_MASK } from '@nesso/ui/primitives/masked-input-field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';

import type {
    ScheduleSessionFormOutput,
    ScheduleSessionFormValues,
} from '../../../model/schedule-session-form.schema';
import { ScheduleSessionLocationPicker } from './schedule-session-location-fields/schedule-session-location-picker';

type Props = {
    control: Control<ScheduleSessionFormValues, unknown, ScheduleSessionFormOutput>;
};

// Each location type's detail is its own form field, so switching types never drops what's
// already typed into another one.
export function ScheduleSessionLocationFields({ control }: Props) {
    const locationType = useWatch({ control, name: 'locationType' });

    return (
        <div className="flex flex-col gap-3">
            <Controller
                control={control}
                name="locationType"
                render={({ field }) => (
                    <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-row items-center gap-3 py-2"
                    >
                        {/* `fieldClassName="w-fit"` overrides `Field`'s own `w-full`, which would
                        stretch every item across the row. */}
                        <RadioGroupItemField
                            value="IN_PERSON"
                            label="In-person"
                            fieldClassName="w-fit"
                        />
                        <RadioGroupItemField
                            value="TELEHEALTH"
                            label="Telehealth"
                            fieldClassName="w-fit"
                        />
                        <RadioGroupItemField value="PHONE" label="Phone" fieldClassName="w-fit" />
                    </RadioGroup>
                )}
            />
            {locationType === 'IN_PERSON' && (
                <Controller
                    control={control}
                    name="practiceLocationId"
                    render={({ field, fieldState }) => (
                        <ScheduleSessionLocationPicker
                            value={field.value}
                            onSelect={field.onChange}
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
            )}
            {locationType === 'TELEHEALTH' && (
                <Controller
                    control={control}
                    name="telehealthLink"
                    render={({ field, fieldState }) => (
                        <ScheduleSessionTelehealthLinkField
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
            )}
            {locationType === 'PHONE' && (
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState }) => (
                        <MaskedInputField
                            {...field}
                            id="schedule-session-phone-number"
                            label="Phone number"
                            labelClassName="sr-only"
                            mask={US_PHONE_MASK}
                            // A blank placeholder, unlike the sample-digit convention elsewhere for
                            // this mask (`edit-client-profile-form-contact.tsx`,
                            // `practice-information-section.tsx`).
                            placeholder="(   )    -    "
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
            )}
        </div>
    );
}

type TelehealthLinkFieldProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    error?: boolean | string;
};

// Composed from `InputGroup` directly, as `DurationField` (`save-service-sheet-form.tsx`) does,
// for an addon no `*-field` wrapper offers — here a leading icon rather than a trailing unit.
function ScheduleSessionTelehealthLinkField({
    value,
    onChange,
    onBlur,
    error,
}: TelehealthLinkFieldProps) {
    const wiring = useFieldWiring({ id: 'schedule-session-telehealth-link', error });

    return (
        <Field data-invalid={wiring.dataInvalid}>
            <FieldLabel htmlFor={wiring.controlId} className="sr-only">
                Telehealth link
            </FieldLabel>
            <InputGroup>
                <InputGroupAddon>
                    <Video className="size-4 text-muted-foreground" />
                </InputGroupAddon>
                <InputGroupInput
                    {...wiring.controlProps}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                    placeholder="telehealth link"
                />
            </InputGroup>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
